import { useEffect, useState } from "react";
import api, { price } from "../lib/api";
import DateRange, { rangeParams } from "../components/DateRange";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus, Search, Check, GitCompare } from "lucide-react";

const COLORS = ["#2C4A3B", "#8B3A3A", "#D4AF37", "#3E5C7E", "#6B8E7A", "#9A6B4F", "#7E6B9A", "#B08968"];

export default function PriceTrends() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]); // array of normalized_name
  const [series, setSeries] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [perUnit, setPerUnit] = useState(false);
  const [refSupplier, setRefSupplier] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      if (data.length) setSelected([data[0].normalized_name]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected.length === 0) { setSeries([]); return; }
    const base = { ...rangeParams(from, to) };
    if (perUnit && selected.length === 1) { base.per_unit = true; if (refSupplier) base.supplier = refSupplier; }
    Promise.all(
      selected.map((nn) =>
        api.get(`/products/${encodeURIComponent(nn)}/trend`, { params: base }).then((r) => ({ nn, points: r.data }))
      )
    ).then((results) => {
      const byDate = {};
      results.forEach(({ nn, points }) => {
        points.forEach((p) => {
          if (!byDate[p.date]) byDate[p.date] = { label: p.date };
          byDate[p.date][nn] = p.unit_price;
        });
      });
      setSeries(Object.values(byDate).sort((a, b) => a.label.localeCompare(b.label)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, from, to, perUnit, refSupplier]);

  const toggle = (nn) => {
    setSelected((prev) => prev.includes(nn) ? prev.filter((x) => x !== nn) : [...prev, nn]);
  };
  const nameOf = (nn) => products.find((p) => p.normalized_name === nn)?.name || nn;

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const isMulti = selected.length > 1;

  // single-product variation
  let change = 0, single = null;
  if (selected.length === 1 && series.length > 0) {
    const nn = selected[0];
    const vals = series.map((s) => s[nn]).filter((v) => v != null);
    if (vals.length >= 2) change = ((vals[vals.length - 1] - vals[0]) / vals[0]) * 100;
    single = nameOf(nn);
  }
  const Trend = change > 0.5 ? TrendingUp : change < -0.5 ? TrendingDown : Minus;
  const trendColor = change > 0.5 ? "text-alert" : change < -0.5 ? "text-brand" : "text-stone-500";

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Analisi</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Andamento prezzi</h1>
          <p className="text-sm text-stone-500 mt-2">Seleziona uno o più prodotti per confrontare l'andamento nel tempo.</p>
        </div>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-stone-200 rounded-sm p-4 lg:max-h-[600px] flex flex-col">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto…"
              className="w-full border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
          </div>
          {selected.length > 0 && (
            <p className="text-xs text-stone-400 mb-2 px-1">{selected.length} selezionati</p>
          )}
          <div className="overflow-y-auto flex-1 space-y-1">
            {filtered.length === 0 && <p className="text-sm text-stone-400 p-2">Nessun prodotto.</p>}
            {filtered.map((p, idx) => {
              const on = selected.includes(p.normalized_name);
              const colorIdx = selected.indexOf(p.normalized_name);
              return (
                <button key={p.normalized_name} data-testid={`product-item-${p.normalized_name}`} onClick={() => toggle(p.normalized_name)}
                  className={`w-full text-left px-3 py-2.5 rounded-sm transition-colors flex items-center gap-3 ${
                    on ? "bg-brand text-white" : "hover:bg-stone-100 text-stone-700"
                  }`}>
                  <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${on ? "bg-white border-white" : "border-stone-300"}`}
                    style={on && colorIdx >= 0 ? { background: COLORS[colorIdx % COLORS.length], borderColor: COLORS[colorIdx % COLORS.length] } : {}}>
                    {on && <Check size={12} className="text-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">{p.name}</span>
                    <span className={`block text-xs ${on ? "text-white/70" : "text-stone-400"}`}>{p.count} rilevazioni · {p.suppliers.length} fornitori</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-stone-200 rounded-sm p-6">
          {selected.length === 0 ? (
            <div className="h-96 flex flex-col items-center justify-center text-stone-400 gap-2">
              <GitCompare size={28} strokeWidth={1.3} />
              <p className="text-sm">Seleziona almeno un prodotto dalla lista.</p>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h3 className="font-serif text-3xl text-stone-900">{isMulti ? "Confronto prodotti" : single}</h3>
                  <p className="text-sm text-stone-500">Prezzo unitario nel tempo</p>
                </div>
                {!isMulti && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label data-testid="per-unit-toggle" className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 cursor-pointer">
                      <input type="checkbox" checked={perUnit} onChange={(e) => setPerUnit(e.target.checked)} /> Prezzo per unità
                    </label>
                    {perUnit && (
                      <select data-testid="ref-supplier" value={refSupplier} onChange={(e) => setRefSupplier(e.target.value)}
                        className="border border-stone-300 rounded-sm px-2 py-1 text-xs">
                        <option value="">Fornitore di rif. (auto)</option>
                        {(products.find((p) => p.normalized_name === selected[0])?.suppliers || []).map((sp) => (
                          <option key={sp} value={sp}>{sp}</option>
                        ))}
                      </select>
                    )}
                    <div className="text-right">
                      <div className={`flex items-center gap-1 justify-end ${trendColor}`}>
                        <Trend size={20} /><span className="font-serif text-3xl font-light">{change > 0 ? "+" : ""}{change.toFixed(1)}%</span>
                      </div>
                      <p className="text-xs text-stone-400">variazione dal primo acquisto</p>
                    </div>
                  </div>
                )}
              </div>
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                  <XAxis dataKey="label" stroke="#A8A29E" fontSize={11} />
                  <YAxis stroke="#A8A29E" fontSize={11} domain={["auto", "auto"]} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(v, name) => [price(v), name]}
                    contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
                  {isMulti && <Legend />}
                  {selected.map((nn, i) => (
                    <Line key={nn} type="monotone" dataKey={nn} name={nameOf(nn)} connectNulls
                      stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
